import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Snackbar } from '@material-ui/core';
import JSZip from 'jszip';

import { getUserId } from '../../helpers/common';
import * as userStationsReducers from '../../system/reducer/UserStationsReducers';
import * as api from '../api';
import DownloadReportPortfolioSelect from './DownloadReportPortfolioSelect';
import DownloadReportPortfolioTable from './DownloadReportPortfolioTable';

class DownloadReportPortfolio extends Component {
  state = {
    selectedStationName: 'All',
    selectedYear: '',
    selectedMonth: '',
    years: [],
    months: [],
    files: [],
    message: '',
    isLoadingYears: false,
    isLoadingMonths: false,
    isSubmitting: false,
    isDownloading: false,
  };

  availabilityRequestId = 0;

  componentDidMount() {
    if (this.props.stations.length > 0) {
      this.loadAvailability('All');
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.stations.length === 0 && this.props.stations.length > 0) {
      this.loadAvailability(this.state.selectedStationName || 'All');
    }
  }

  componentWillUnmount() {
    this.availabilityRequestId += 1;
  }

  getStationNames = stationName => {
    if (stationName === 'All') {
      return this.props.stations.map(station => station.name);
    }
    return [stationName];
  };

  isPortfolioRequest = stationName => stationName === 'All';

  showMessage = message => this.setState({ message: message || 'Unable to complete the request.' });

  loadAvailability = (stationName, year) => {
    if (!stationName) return Promise.resolve();

    const requestId = ++this.availabilityRequestId;
    const loadingState = year
      ? { isLoadingMonths: true }
      : { isLoadingYears: true, isLoadingMonths: false };
    this.setState(loadingState);

    const isPortfolio = this.isPortfolioRequest(stationName);
    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(stationName) : stationName,
    };
    if (year) request.year = String(year);

    const check = isPortfolio
      ? api.checkPortfolioDownloadReportObject
      : api.checkDownloadReportObject;

    return check(request)
      .then(response => {
        if (requestId !== this.availabilityRequestId) return;
        if (response.status !== 200) {
          this.showMessage(response.message);
          return;
        }
        const data = response.data || {};
        this.setState({
          years: data.year || [],
          months: year ? (data.month || []) : [],
        });
      })
      .catch(error => {
        if (requestId === this.availabilityRequestId) this.showMessage(error.message);
      })
      .then(() => {
        if (requestId !== this.availabilityRequestId) return;
        this.setState(year ? { isLoadingMonths: false } : { isLoadingYears: false });
      });
  };

  handleStationChange = selectedStationName => {
    this.setState({
      selectedStationName,
      selectedYear: '',
      selectedMonth: '',
      years: [],
      months: [],
      files: [],
    });
    this.loadAvailability(selectedStationName);
  };

  handleYearChange = selectedYear => {
    this.setState({ selectedYear, selectedMonth: '', months: [], files: [] });
    this.loadAvailability(this.state.selectedStationName, selectedYear);
  };

  handleMonthChange = selectedMonth => this.setState({ selectedMonth, files: [] });

  handleReset = () => {
    this.availabilityRequestId += 1;
    this.setState({
      selectedStationName: '',
      selectedYear: '',
      selectedMonth: '',
      years: [],
      months: [],
      files: [],
      isLoadingYears: false,
      isLoadingMonths: false,
    });
  };

  handleSubmit = () => {
    const { selectedStationName, selectedYear, selectedMonth } = this.state;
    const isPortfolio = this.isPortfolioRequest(selectedStationName);
    if (!selectedStationName || !selectedYear || (isPortfolio && !selectedMonth)) return;

    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(selectedStationName) : selectedStationName,
      year: String(selectedYear),
    };
    if (selectedMonth) request.month = selectedMonth;

    const list = isPortfolio
      ? api.listPortfolioDownloadReportObject
      : api.listDownloadReportObject;

    this.setState({ isSubmitting: true, files: [] });
    list(request)
      .then(response => {
        if (response.status !== 200) {
          this.showMessage(response.message);
          return;
        }
        this.setState({ files: (response.data && response.data.files) || [] });
      })
      .catch(error => this.showMessage(error.message))
      .then(() => this.setState({ isSubmitting: false }));
  };

  extractFileName = file => {
    const path = typeof file === 'string'
      ? file
      : (file.fileName || file.path || file.key || '');
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  downloadLinksAsZip = (links, selectedFiles) => {
    const selectedNames = selectedFiles.map(this.extractFileName);
    const selectedNameSet = new Set(selectedNames);
    const filteredLinks = links.filter(link => selectedNameSet.has(link.fileName));

    if (filteredLinks.length === 0) {
      this.showMessage('No download links were returned for the selected reports.');
      return Promise.resolve();
    }

    const zip = new JSZip();
    return Promise.all(filteredLinks.map(fileObject =>
      fetch(fileObject.downloadDataLink)
        .then(response => {
          if (!response.ok) throw new Error(`Unable to download ${fileObject.fileName}`);
          return response.blob();
        })
        .then(blob => zip.file(fileObject.fileName, blob))
    ))
      .then(() => zip.generateAsync({ type: 'blob' }))
      .then(content => {
        const { selectedStationName, selectedYear, selectedMonth } = this.state;
        const stationLabel = selectedStationName === 'All' ? 'Portfolio' : selectedStationName;
        const periodLabel = selectedMonth || selectedYear;
        const zipFileName = `SERIS_Download_Report_${stationLabel}_${periodLabel}.zip`;
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.download = zipFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
  };

  handleDownload = selectedFiles => {
    const { selectedStationName, selectedYear, selectedMonth } = this.state;
    if (selectedFiles.length === 0) return;

    const isPortfolio = this.isPortfolioRequest(selectedStationName);
    const request = {
      userId: getUserId(),
      stationName: isPortfolio ? this.getStationNames(selectedStationName) : selectedStationName,
      year: String(selectedYear),
    };
    if (selectedMonth) request.month = selectedMonth;

    const generate = isPortfolio
      ? api.generatePortfolioDownloadReportLink
      : api.generateDownloadReportLink;

    this.setState({ isDownloading: true });
    generate(request)
      .then(response => {
        if (response.status !== 200) {
          this.showMessage(response.message);
          return null;
        }
        const links = (response.data && response.data.downloadDataLinks) || [];
        return this.downloadLinksAsZip(links, selectedFiles);
      })
      .catch(error => this.showMessage(error.message))
      .then(() => this.setState({ isDownloading: false }));
  };

  render() {
    const { stations, t } = this.props;
    const {
      selectedStationName, selectedYear, selectedMonth, years, months, files,
      message, isLoadingYears, isLoadingMonths, isSubmitting, isDownloading,
    } = this.state;

    return (
      <div className="downloadReportPortfolio">
        <DownloadReportPortfolioSelect
          stations={stations}
          selectedStationName={selectedStationName}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          years={years}
          months={months}
          isLoadingYears={isLoadingYears}
          isLoadingMonths={isLoadingMonths}
          isSubmitting={isSubmitting}
          onStationChange={this.handleStationChange}
          onYearChange={this.handleYearChange}
          onMonthChange={this.handleMonthChange}
          onSubmit={this.handleSubmit}
          onReset={this.handleReset}
        />
        <DownloadReportPortfolioTable
          files={files}
          year={selectedYear}
          month={selectedMonth}
          isPortfolio={selectedStationName === 'All'}
          isLoading={isSubmitting}
          isDownloading={isDownloading}
          onDownload={this.handleDownload}
        />
        <Snackbar
          open={Boolean(message)}
          autoHideDuration={5000}
          onClose={() => this.setState({ message: '' })}
          message={<span>{message || t('error')}</span>}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  stations: userStationsReducers.getStations(state),
});

export default connect(mapStateToProps)(withTranslation()(DownloadReportPortfolio));
